import { Input } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MediaUpload, filesUploader, type MediaUploadValue } from '../MediaUpload';
import { resolveAssetUrl } from '@shared/utils/assetUrl';

export interface SeoPanelValue {
  title: string;
  description: string;
  slug: string;
  ogImage: MediaUploadValue | null;
}

interface SeoPanelProps {
  value: SeoPanelValue;
  onChange: (next: SeoPanelValue) => void;
  baseUrl?: string;
  maxTitleLength?: number;
  maxDescriptionLength?: number;
}

function counterState(len: number, max: number): 'ok' | 'warn' | 'error' {
  if (len > max) return 'error';
  if (len > max * 0.9) return 'warn';
  return 'ok';
}

export function SeoPanel({
  value,
  onChange,
  baseUrl = 'https://emlakkatilim.com.tr',
  maxTitleLength = 60,
  maxDescriptionLength = 160,
}: SeoPanelProps) {
  const { t } = useTranslation('components');
  const titleState = useMemo(() => counterState(value.title.length, maxTitleLength), [value.title, maxTitleLength]);
  const descState = useMemo(
    () => counterState(value.description.length, maxDescriptionLength),
    [value.description, maxDescriptionLength],
  );
  const domain = baseUrl.replace(/^https?:\/\//, '').replace(/\/$/, '');

  return (
    <div className="ds-seo">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <label>
          <div style={{ fontSize: 12, color: 'var(--ds-text-tertiary)', marginBottom: 4 }}>{t('seoPanel.metaTitle')}</div>
          <Input
            value={value.title}
            onChange={(e) => onChange({ ...value, title: e.target.value })}
            maxLength={maxTitleLength + 20}
            placeholder={t('seoPanel.metaTitlePlaceholder')}
          />
          <div className="ds-seo__counter" data-state={titleState}>
            {value.title.length} / {maxTitleLength}
          </div>
        </label>
        <label>
          <div style={{ fontSize: 12, color: 'var(--ds-text-tertiary)', marginBottom: 4 }}>{t('seoPanel.metaDescription')}</div>
          <Input.TextArea
            value={value.description}
            onChange={(e) => onChange({ ...value, description: e.target.value })}
            rows={3}
            maxLength={maxDescriptionLength + 40}
            placeholder={t('seoPanel.metaDescriptionPlaceholder')}
          />
          <div className="ds-seo__counter" data-state={descState}>
            {value.description.length} / {maxDescriptionLength}
          </div>
        </label>
        <div>
          <div style={{ fontSize: 12, color: 'var(--ds-text-tertiary)', marginBottom: 4 }}>{t('seoPanel.ogImage')}</div>
          <MediaUpload
            value={value.ogImage}
            onChange={(og) => onChange({ ...value, ogImage: og })}
            uploader={filesUploader}
          />
        </div>
      </div>

      <div className="ds-seo__preview">
        <div className="ds-seo__preview-card">
          <h5>{t('seoPanel.googlePreview')}</h5>
          <div className="ds-seo__google-url">{domain} › {value.slug || t('seoPanel.slugFallback')}</div>
          <p className="ds-seo__google-title">{value.title || t('seoPanel.titleFallback')}</p>
          <p className="ds-seo__google-desc">
            {value.description || t('seoPanel.descriptionFallback')}
          </p>
        </div>
        {value.ogImage?.url ? (
          <div className="ds-seo__preview-card">
            <h5>{t('seoPanel.ogCard')}</h5>
            <div className="ds-seo__og">
              <div
                className="ds-seo__og-img"
                style={{ backgroundImage: `url(${resolveAssetUrl(value.ogImage.url)})` }}
                role="img"
                aria-label={t('seoPanel.ogImageLabel')}
              />
              <div className="ds-seo__og-body">
                <div className="ds-seo__og-domain">{domain}</div>
                <div className="ds-seo__og-title">{value.title || t('seoPanel.ogTitleFallback')}</div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
